import {
  ChangeEvent,
  MouseEvent,
  ReactNode,
  SyntheticEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import TextInput from "components/ui/TextInput";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import { ExpandMore, MoreVert } from "@mui/icons-material";
import { DictionaryMode } from "contexts/DictionaryToolsContext";
import {
  GetPersonalDictionaryResponse,
  personalDictionaryQuery,
  updateDictionary,
  useDeleteSavedWord,
} from "services/dictionary";
import { logError } from "services/logging";
import Button from "components/ui/Button";
import DefineIcon from "images/define.svg?react";
import TranslateIcon from "images/translate.svg?react";
import trash from "images/trash.svg";
import quickReferenceAllImg from "images/quick_reference_all.svg";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import { SavedWordInput, WordFormData } from "./model";
import { SentencesExampleDialog } from "./SentencesExampleDialog";
import "./SaveWords.css";

type SaveWordsProps = SavedWordInput & {
  expanded: boolean;
  onModeChanged: (mode: DictionaryMode) => void;
  onDeleteWord: (word: WordFormData) => void;
  onTextChanged: (text: string) => void;
  onChangePanel: (event: SyntheticEvent, expanded: boolean) => void;
};

export const SaveWords = (props: SaveWordsProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const deleteWord = useDeleteSavedWord();
  const updateWord = useMutation({ mutationFn: updateDictionary });
  const [openExampleDialog, setOpenExampleDialog] = useState(false);
  const [delayedNotes, setDelayedNotes] = useState<string | null>(null);
  const [wordData, setWordData] = useState<WordFormData>({
    id: props.id || "",
    pos: props.pos || "",
    word: props.word || "",
    definition: props.definition || "",
    translation: props.translation || "",
    audio: props.audio || "",
    notes: props.notes || "",
  });

  useEffect(() => {
    setWordData({
      id: props.id || "",
      pos: props.pos || "",
      word: props.word || "",
      definition: props.definition || "",
      translation: props.translation || "",
      audio: props.audio || "",
      notes: props.notes || "",
    });
  }, [props]);

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setWordData({ ...wordData, [e.target.name]: e.target.value });
  };

  const handleDelete = (e: MouseEvent) => {
    e.stopPropagation();
    if (!wordData.id) {
      return;
    }
    deleteWord.mutate(wordData.id, {
      onSuccess: () => props.onDeleteWord(wordData),
    });
  };

  const handleShowExampleSentences = () => {
    if (props.id) {
      setOpenExampleDialog(true);
    }
  };

  const patchCachedNotes = (id: string, notes: string) => {
    // Patch every cached page in place instead of invalidating, so saving
    // notes doesn't refetch the list under the user while they keep typing.
    queryClient.setQueriesData<GetPersonalDictionaryResponse>(
      { queryKey: personalDictionaryQuery.baseKey },
      data =>
        data && {
          ...data,
          list: data.list.map(word => (word.id === id ? { ...word, notes } : word)),
        }
    );
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => setDelayedNotes(wordData.notes), 500);
    return () => clearTimeout(timeoutId);
  }, [wordData.notes]);

  useEffect(() => {
    if (wordData.id && props.expanded && delayedNotes !== null) {
      const id = wordData.id;
      updateWord.mutate(
        { id, notes: delayedNotes },
        {
          onSuccess: () => patchCachedNotes(id, delayedNotes),
          onError: logError,
        }
      );
    }
    // Fires on the debounced value alone, so typing does not save every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delayedNotes]);

  return (
    <div className="save-item">
      <AccordionView
        expanded={props.expanded}
        onChange={props.onChangePanel}
        header={
          <SaveWordHeader
            pos={props.pos}
            word={props.word}
            audio={props.audio}
            onDelete={handleDelete}
            onModeChanged={props.onModeChanged}
            onTextChanged={props.onTextChanged}
            onChangePanel={props.onChangePanel}
          />
        }
      >
        <MyNotes
          value={wordData.notes}
          label={t("Dictionary.savedWords.notesLabel")}
          onChange={onChange}
        />
        <button className="explain-button" onClick={handleShowExampleSentences}>
          <img src={quickReferenceAllImg} alt="quick-reference-all" />
          <span>{t("Dictionary.savedWords.showExamples")}</span>
        </button>
      </AccordionView>
      {openExampleDialog && props.id && (
        <SentencesExampleDialog
          open={openExampleDialog}
          word={{ id: props.id, word: props.word }}
          onClose={() => setOpenExampleDialog(false)}
        />
      )}
    </div>
  );
};

type SaveWordHeaderProps = {
  audio?: string | null;
  word: string;
  pos: string | null;
  onDelete: (e: MouseEvent) => void;
  onModeChanged?: (mode: DictionaryMode) => void;
  onTextChanged?: (text: string) => void;
  onChangePanel?: (event: SyntheticEvent, expanded: boolean) => void;
};

const SaveWordHeader = (props: SaveWordHeaderProps) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const onModeChanged = (mode: DictionaryMode) => () => {
    if (props.onTextChanged) {
      props.onTextChanged(props.word);
    }

    setTimeout(() => {
      if (props.onModeChanged) {
        props.onModeChanged(mode);
      }
    }, 100);
  };

  const handleOpenActionMenu = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const handleCloseActionMenu = () => {
    setAnchorEl(null);
  };

  const handleModeChanged = (event: MouseEvent, mode: DictionaryMode) => {
    event.stopPropagation();
    onModeChanged(mode)();
    handleCloseActionMenu();
  };

  const handleDelete = (event: MouseEvent) => {
    event.stopPropagation();
    props.onDelete(event);
    handleCloseActionMenu();
  };

  return (
    <div className="save-word-header">
      <div className="save-word-header-container">
        <Pronunciation source={props.audio} />
        <div className="sub-header-container">
          <span>
            <span className="def title" title={props.word}>
              {props.word}
            </span>
          </span>
          <span className="speech" title={props.pos ?? undefined}>
            {props.pos}
          </span>
        </div>
      </div>
      <div className="save-word-header-actions">
        <Button
          variant="plain"
          className="action-with-image"
          onClick={onModeChanged("define")}
          aria-label={t("Dictionary.panel.modeDefine")}
        >
          <DefineIcon className="mode-icon" />
        </Button>
        <Button
          variant="plain"
          className="action-with-image"
          onClick={onModeChanged("translate")}
          aria-label={t("Dictionary.panel.modeTranslate")}
        >
          <TranslateIcon className="mode-icon" />
        </Button>
        <Button
          variant="plain"
          className="action-with-image"
          onClick={props.onDelete}
          aria-label={t("Dictionary.savedWords.delete")}
        >
          <img src={trash} height={20} width={18} alt="" />
        </Button>
      </div>
      <div className="save-word-header-mobile-actions">
        <IconButton className="save-word-header-expand" size="large">
          <ExpandMore />
        </IconButton>
        <IconButton className="menu-button" onClick={handleOpenActionMenu} size="large">
          <MoreVert />
        </IconButton>
        <Menu
          className="save-words-mobile-action-menu"
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleCloseActionMenu}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <MenuItem onClick={(event: MouseEvent) => handleModeChanged(event, "define")}>
            <DefineIcon className="mode-icon" />
            {t("Dictionary.panel.modeDefine")}
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={(event: MouseEvent) => handleModeChanged(event, "translate")}
          >
            <TranslateIcon className="mode-icon" />
            {t("Dictionary.panel.modeTranslate")}
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleDelete}>
            <img src={trash} height={20} width={18} alt="" />
            {t("Dictionary.savedWords.delete")}
          </MenuItem>
        </Menu>
      </div>
    </div>
  );
};

type PronunciationProps = {
  source?: string | null;
};

const Pronunciation = (props: PronunciationProps) => {
  const audioRef = useRef<HTMLAudioElement>(null);

  const onAudioPlay = (e: MouseEvent) => {
    e.stopPropagation();
    // Rejects when a second play interrupts the first, which is what tapping
    // another word does. Nothing to report and nothing to recover.
    audioRef.current?.play().catch(() => {});
  };

  return (
    <div className={"audio-container"}>
      <Button variant="plain" className="audio-btn" onClick={onAudioPlay}>
        <VolumeUpIcon sx={{ fontSize: 18, color: "var(--action-brand)" }} />
      </Button>
      <audio controlsList="nodownload" ref={audioRef}>
        <source src={props.source ?? undefined} />
      </audio>
    </div>
  );
};

type MyNotesProps = {
  value: string;
  label: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
};

const MyNotes = (props: MyNotesProps) => {
  const { t } = useTranslation();
  return (
    <div className={"my-notes-text"}>
      <TextInput
        label={props.label}
        id="outlined-basic"
        name="notes"
        value={props.value}
        onChange={props.onChange}
        placeholder={t("Dictionary.savedWords.notesPlaceholder")}
      />
    </div>
  );
};

type AccordionViewProps = {
  header: ReactNode;
  expanded: boolean;
  children: ReactNode;
  onChange: (event: SyntheticEvent, expanded: boolean) => void;
};

const AccordionView = (props: AccordionViewProps) => {
  return (
    <Accordion
      className="accordion-container"
      expanded={props.expanded}
      onChange={props.onChange}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        aria-controls="panel-content"
        id="panel-header"
        className="accordion-summary"
      >
        {props.header}
      </AccordionSummary>
      <AccordionDetails>
        <div className="accordion-details">{props.children}</div>
      </AccordionDetails>
    </Accordion>
  );
};
